import { useEffect, useState } from "react"
import type { ApiBundle } from "../paygate"

export const COUNTDOWN_MS = 10 * 60 * 1000
export const STORAGE_TTL_MS = 24 * 60 * 60 * 1000

type InternalState = {
  deadline: number | null
  remainingMs: number
  selectedPlan: string
  apiPlans: ApiBundle[]
}

type Store = {
  state: InternalState
  listeners: Set<(state: InternalState) => void>
  intervalId: number | null
}

const STORAGE_KEY_PREFIX = "paygate2SharedState"

const makeKey = (surveyId: string) => `${STORAGE_KEY_PREFIX}:${surveyId}`

const loadFromStorage = (
  surveyId: string,
  defaultPlan: string,
): Pick<InternalState, "deadline" | "selectedPlan" | "apiPlans"> => {
  if (typeof window === "undefined") {
    return {
      deadline: null,
      selectedPlan: defaultPlan,
      apiPlans: [],
    }
  }

  try {
    const raw = window.localStorage.getItem(makeKey(surveyId))
    if (!raw) {
      return {
        deadline: null,
        selectedPlan: defaultPlan,
        apiPlans: [],
      }
    }

    const parsed = JSON.parse(raw) as {
      deadline: number | null
      selectedPlan?: string
      apiPlans?: ApiBundle[]
      ttl?: number
    }

    const now = Date.now()
    const storedPlan = parsed.selectedPlan || defaultPlan
    const storedApiPlans = parsed.apiPlans ?? []

    if (typeof parsed.ttl === "number") {
      if (now >= parsed.ttl) {
        window.localStorage.removeItem(makeKey(surveyId))
        return {
          deadline: null,
          selectedPlan: defaultPlan,
          apiPlans: [],
        }
      }

      return {
        deadline: parsed.deadline ?? null,
        selectedPlan: storedPlan,
        apiPlans: storedApiPlans,
      }
    }

    if (parsed.deadline && now >= parsed.deadline) {
      window.localStorage.removeItem(makeKey(surveyId))
      return {
        deadline: null,
        selectedPlan: defaultPlan,
        apiPlans: [],
      }
    }

    return {
      deadline: parsed.deadline ?? null,
      selectedPlan: storedPlan,
      apiPlans: storedApiPlans,
    }
  } catch {
    return {
      deadline: null,
      selectedPlan: defaultPlan,
      apiPlans: [],
    }
  }
}

const saveToStorage = (surveyId: string, state: InternalState) => {
  if (typeof window === "undefined") return

  try {
    window.localStorage.setItem(
      makeKey(surveyId),
      JSON.stringify({
        deadline: state.deadline,
        selectedPlan: state.selectedPlan,
        apiPlans: state.apiPlans,
        ttl: Date.now() + STORAGE_TTL_MS,
      }),
    )
  } catch {
    // ignore
  }
}

const stores = new Map<string, Store>()

const createStore = (surveyId: string, defaultPlan: string): Store => {
  const { deadline, selectedPlan, apiPlans } = loadFromStorage(
    surveyId,
    defaultPlan,
  )

  const initialRemainingMs =
    deadline != null ? Math.max(0, deadline - Date.now()) : COUNTDOWN_MS

  const store: Store = {
    state: {
      deadline,
      remainingMs: initialRemainingMs,
      selectedPlan,
      apiPlans,
    },
    listeners: new Set(),
    intervalId: null,
  }

  const notify = () => {
    store.listeners.forEach((listener) => {
      listener(store.state)
    })
  }

  const startIntervalIfNeeded = () => {
    if (store.intervalId != null) return
    if (typeof window === "undefined") return

    const id = window.setInterval(() => {
      const { deadline } = store.state
      if (!deadline) return

      const msLeft = deadline - Date.now()

      if (msLeft <= 0) {
        store.state = {
          ...store.state,
          remainingMs: 0,
        }
        saveToStorage(surveyId, store.state)
        notify()
        return
      }

      store.state = {
        ...store.state,
        remainingMs: msLeft,
      }
      saveToStorage(surveyId, store.state)
      notify()
    }, 1000)

    store.intervalId = id
  }

  const startCountdown = () => {
    const now = Date.now()
    const newDeadline = now + COUNTDOWN_MS

    store.state = {
      ...store.state,
      deadline: newDeadline,
      remainingMs: COUNTDOWN_MS,
    }

    saveToStorage(surveyId, store.state)
    startIntervalIfNeeded()
    notify()
  }

  const setSelectedPlan = (plan: string) => {
    store.state = {
      ...store.state,
      selectedPlan: plan,
    }
    saveToStorage(surveyId, store.state)
    notify()
  }

  const setApiPlans = (plans: ApiBundle[]) => {
    store.state = {
      ...store.state,
      apiPlans: plans,
    }
    saveToStorage(surveyId, store.state)
    notify()
  }

  ;(store as any).startCountdown = startCountdown
  ;(store as any).setSelectedPlan = setSelectedPlan
  ;(store as any).setApiPlans = setApiPlans
  ;(store as any).notify = notify
  ;(store as any).startIntervalIfNeeded = startIntervalIfNeeded

  if (deadline) {
    startIntervalIfNeeded()
  }

  return store
}

const getStore = (
  surveyId: string,
  defaultPlan: string,
): Store & {
  startCountdown: () => void
  setSelectedPlan: (plan: string) => void
  setApiPlans: (plans: ApiBundle[]) => void
  notify: () => void
  startIntervalIfNeeded: () => void
} => {
  let store = stores.get(surveyId)
  if (!store) {
    store = createStore(surveyId, defaultPlan)
    stores.set(surveyId, store)
  }
  return store as any
}

export type Paygate2SharedState = {
  remainingMs: number
  formattedCountdown: string
  isCountdownActive: boolean
  selectedPlan: string
  apiPlans: ApiBundle[]
  startCountdown: () => void
  setSelectedPlan: (plan: string) => void
  setApiPlans: (plans: ApiBundle[]) => void
  originalPrices: number[]
}

export const usePaygate2ScreenSharedState = (
  surveyId: string,
  defaultPlan: string,
): Paygate2SharedState => {
  const store = getStore(surveyId, defaultPlan)
  const [state, setState] = useState<InternalState>(store.state)

  useEffect(() => {
    const listener = (nextState: InternalState) => {
      setState(nextState)
    }

    store.listeners.add(listener)
    listener(store.state)

    return () => {
      store.listeners.delete(listener)
    }
  }, [store])

  const { remainingMs, deadline, selectedPlan, apiPlans } = state

  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000))
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60

  const formattedCountdown = `${String(minutes).padStart(2, "0")}:${String(
    seconds,
  ).padStart(2, "0")}`

  const isCountdownActive = Boolean(deadline && remainingMs > 0)

  return {
    remainingMs,
    formattedCountdown,
    isCountdownActive,
    selectedPlan,
    apiPlans,
    startCountdown: (store as any).startCountdown,
    setSelectedPlan: (store as any).setSelectedPlan,
    setApiPlans: (store as any).setApiPlans,
    originalPrices: [13.49, 29.99, 79.99],
  }
}
